import type { Artist, Venue } from "../types";
import { artistPrimaryCity } from "./artistProfile";
import { venuePrimaryCity } from "./venueAddress";
import { expectedStateForCity, getCitiesForState, getDistrictsForState } from "./indianLocationHierarchy";
import {
  INDIAN_STATES_AND_UTS,
  isIndianStateName,
  isKnownIndianLocation,
  POPULAR_INDIAN_LOCATIONS,
} from "./indianLocations";

export type LocationOptionGroup = {
  label: string;
  options: readonly string[];
};

function collectCustomCities(venues: Venue[], artists: Artist[]): string[] {
  const set = new Set<string>();
  venues.forEach((venue) => {
    const city = venuePrimaryCity(venue);
    if (city && !isKnownIndianLocation(city) && !isIndianStateName(city)) set.add(city);
  });
  artists.forEach((artist) => {
    const city = artistPrimaryCity(artist);
    if (city && !isKnownIndianLocation(city) && !isIndianStateName(city)) set.add(city);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function collectCustomDistricts(venues: Venue[], artists: Artist[], state: string): string[] {
  const stateKey = state.trim();
  if (!stateKey) return [];
  const known = new Set(getDistrictsForState(stateKey).map((d) => d.toLowerCase()));
  const set = new Set<string>();
  const addIfMatch = (entityState?: string | null, district?: string | null) => {
    if (entityState?.trim() !== stateKey) return;
    const d = district?.trim();
    if (!d || known.has(d.toLowerCase())) return;
    set.add(d);
  };
  venues.forEach((v) => addIfMatch(v.state, v.district));
  artists.forEach((a) => addIfMatch(a.state, a.district));
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** State / UT dropdown groups. */
export function getStateOptionGroups(): LocationOptionGroup[] {
  return [
    {
      label: "States & union territories",
      options: INDIAN_STATES_AND_UTS.slice().sort((a, b) => a.localeCompare(b)),
    },
  ];
}

/** District dropdown for a selected state. */
export function getDistrictOptionGroups(
  state: string,
  venues: Venue[],
  artists: Artist[],
): LocationOptionGroup[] {
  const districts = getDistrictsForState(state);
  const custom = collectCustomDistricts(venues, artists, state);
  const groups: LocationOptionGroup[] = [];
  if (districts.length > 0) {
    groups.push({ label: "Districts", options: districts });
  }
  if (custom.length > 0) {
    groups.push({ label: "From your registry", options: custom });
  }
  return groups;
}

/** City combobox / datalist options for a selected state. */
export function getCityOptionGroups(
  state: string,
  venues: Venue[],
  artists: Artist[],
): LocationOptionGroup[] {
  const stateKey = state.trim();
  const groups: LocationOptionGroup[] = [];

  if (stateKey) {
    const forState = getCitiesForState(stateKey);
    if (forState.length > 0) {
      groups.push({ label: `Cities in ${stateKey}`, options: forState });
    }
  } else {
    groups.push({ label: "Popular cities", options: POPULAR_INDIAN_LOCATIONS });
  }

  const custom = collectCustomCities(venues, artists);
  const customForState = stateKey
    ? custom.filter((city) => {
        const hint = expectedStateForCity(city);
        return !hint || hint === stateKey;
      })
    : custom;

  if (customForState.length > 0) {
    groups.push({ label: "From your registry", options: customForState });
  }

  return groups;
}

/** Flat city options (for datalist). */
export function collectCityOptions(state: string, venues: Venue[], artists: Artist[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const group of getCityOptionGroups(state, venues, artists)) {
    for (const option of group.options) {
      const key = option.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(option);
      }
    }
  }
  return result;
}

/** @deprecated Use getCityOptionGroups or getStateOptionGroups instead. */
export function getLocationOptionGroups(venues: Venue[], artists: Artist[]): LocationOptionGroup[] {
  return getCityOptionGroups("", venues, artists);
}

/** @deprecated Use collectCityOptions instead. */
export function collectLocationOptions(venues: Venue[], artists: Artist[]): string[] {
  return collectCityOptions("", venues, artists);
}
